// All-requests table — links to /customer/requests/:id detail pages.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Filter as FilterIcon, Plus, Search, Loader2, Package } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState, StatusPill } from '../../components/shared/primitives';
import { CUSTOMER_NAV } from '../_navs';
import { requests, type RequestStatus, type ServiceRequestWithItems } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

const STATUS_FILTERS: Array<{ value: 'all' | RequestStatus; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'quote_pending', label: 'Quote pending' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'paid', label: 'Paid' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function CustomerRequestsPage() {
  const q = useQuery({ queryKey: ['me', 'requests'], queryFn: () => requests.list() });
  const [statusFilter, setStatusFilter] = useState<'all' | RequestStatus>('all');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    let list = q.data ?? [];
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        String(r.id).includes(s) ||
        (r.recipient?.full_name ?? '').toLowerCase().includes(s) ||
        (r.title ?? '').toLowerCase().includes(s) ||
        (r.category?.label ?? '').toLowerCase().includes(s)
      );
    }
    return [...list].sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
  }, [q.data, statusFilter, search]);

  return (
    <PortalShell
      nav={CUSTOMER_NAV}
      activeId="requests"
      title="Requests"
      subtitle="Everything you've sent on behalf of family."
      actions={
        <Link to="/customer/requests/new" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-600 text-white text-[12px] font-semibold hover:bg-brand-700">
          <Plus className="w-3.5 h-3.5" /> New request
        </Link>
      }
    >
      <div className="space-y-4">
        {/* Filter row */}
        <Card padding="p-3" className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by id, recipient, category, title…"
              className="w-full pl-9 pr-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          <FilterIcon className="w-4 h-4 text-slate-400 ml-1" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | RequestStatus)}
            className="text-[12.5px] py-2 px-2 rounded-md border border-slate-200 bg-white"
          >
            {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Card>

        {/* Table */}
        <Card padding="p-0">
          {q.isLoading ? (
            <div className="text-center py-12 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Package}
              title="No requests match"
              body={statusFilter !== 'all' || search.trim() ? 'Try a different filter or clear search.' : 'Build a cart for a family member to get started.'}
              action={<Link to="/customer/requests/new" className="inline-flex items-center gap-1 text-[13px] font-semibold text-brand-600 hover:underline">New request →</Link>}
            />
          ) : (
            <>
              {/* Mobile: stacked cards */}
              <div className="sm:hidden divide-y divide-slate-100">
                {rows.map((r) => <RequestCard key={r.id} r={r} />)}
              </div>
              {/* sm+: table */}
              <table className="hidden sm:table text-[12.5px] w-full">
                <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                  <tr>
                    <th className="text-left py-2.5 px-4 font-medium">Request</th>
                    <th className="text-left py-2.5 px-3 font-medium">For</th>
                    <th className="text-left py-2.5 px-3 font-medium">Status</th>
                    <th className="text-left py-2.5 px-3 font-medium">Items</th>
                    <th className="text-right py-2.5 px-4 font-medium">HTG</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => <RequestRow key={r.id} r={r} />)}
                </tbody>
              </table>
            </>
          )}
        </Card>
      </div>
    </PortalShell>
  );
}

// Drafts link back into the builder to finish; everything else opens detail.
function hrefFor(r: ServiceRequestWithItems) {
  return r.status === 'draft' ? `/customer/requests/new?draft=${r.id}` : `/customer/requests/${r.id}`;
}

function RequestCard({ r }: { r: ServiceRequestWithItems }) {
  const href = hrefFor(r);
  return (
    <Link to={href} className="block px-4 py-3 active:bg-slate-50">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[12px] text-cyan-700">REQ-{String(r.id).padStart(4, '0')}</span>
        <StatusPill status={r.status} />
      </div>
      <p className="mt-1 text-[13.5px] font-semibold text-slate-900 truncate">{r.title || r.category?.label || `Cat ${r.category_id}`}</p>
      <div className="mt-1 flex items-center justify-between text-[12px] text-slate-500">
        <span>For {r.recipient?.full_name?.split(' ')[0] ?? '—'} · {r.cart_item_count > 0 ? `${r.cart_item_count} items` : '—'}</span>
        {r.status === 'draft'
          ? <span className="font-semibold text-brand-600">Continue →</span>
          : <span className="font-mono text-slate-900">{r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor) : '—'}</span>}
      </div>
    </Link>
  );
}

function RequestRow({ r }: { r: ServiceRequestWithItems }) {
  const href = hrefFor(r);
  return (
    <tr className="hover:bg-slate-50 cursor-pointer">
      <td className="py-2.5 px-4">
        <Link to={href} className="block">
          <span className="font-mono text-cyan-700">REQ-{String(r.id).padStart(4, '0')}</span>
          <span className="text-slate-600"> · {r.category?.label ?? `Cat ${r.category_id}`}</span>
          {r.title && <p className="text-[11px] text-slate-500 mt-0.5">{r.title}</p>}
        </Link>
      </td>
      <td className="py-2.5 px-3"><Link to={href} className="block text-slate-700">{r.recipient?.full_name?.split(' ')[0] ?? '—'}</Link></td>
      <td className="py-2.5 px-3"><Link to={href} className="block"><StatusPill status={r.status} /></Link></td>
      <td className="py-2.5 px-3"><Link to={href} className="block text-slate-600">{r.cart_item_count > 0 ? `${r.cart_item_count} items` : '—'}</Link></td>
      <td className="py-2.5 px-4 text-right">
        <Link to={href} className="block font-mono text-slate-900">
          {r.status === 'draft'
            ? <span className="font-sans font-semibold text-brand-600">Continue →</span>
            : r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor, { withSymbol: false }) : <span className="text-slate-400">—</span>}
        </Link>
      </td>
    </tr>
  );
}
